import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/StatusBadge";
import { Plus, Edit, Power, Trash2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const TITLES = {
  categories: "Kategoriler",
  brands: "Markalar",
  "service-areas": "Hizmet Alanları",
};
const KIND_MAP = {
  categories: "categories",
  brands: "brands",
  "service-areas": "service_areas",
};

export default function Definitions() {
  const { kind } = useParams();
  const apiKind = KIND_MAP[kind];
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", is_active: true });

  const load = async () => {
    if (!apiKind) return;
    const { data } = await api.get(`/definitions/${apiKind}`);
    setItems(data);
  };
  useEffect(() => { load(); }, [kind]);

  const openCreate = () => { setEdit(null); setForm({ name: "", description: "", is_active: true }); setOpen(true); };
  const openEdit = (it) => { setEdit(it); setForm({ name: it.name, description: it.description || "", is_active: it.is_active }); setOpen(true); };

  const save = async () => {
    try {
      if (edit) await api.put(`/definitions/${apiKind}/${edit.id}`, form);
      else await api.post(`/definitions/${apiKind}`, form);
      toast.success("Kaydedildi");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hata");
    }
  };

  const deactivate = async (id) => {
    if (!window.confirm("Bu kaydı kalıcı olarak silmek istediğinize emin misiniz?")) return;
    await api.delete(`/definitions/${apiKind}/${id}`);
    toast.success("Silindi");
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-extrabold text-[#08285A]">{TITLES[kind]}</h2>
          <p className="text-xs text-[#6B7280]">Toplam {items.length} kayıt</p>
        </div>
        <Button onClick={openCreate} data-testid={`new-${kind}-btn`} className="bg-[#0B3676] hover:bg-[#08285A] text-white">
          <Plus size={16} className="mr-1" /> Yeni
        </Button>
      </div>

      <div className="azd-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F6F8FB] text-[11px] uppercase tracking-wider text-[#6B7280]">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Ad</th>
              <th className="text-left px-4 py-3 font-semibold">Açıklama</th>
              <th className="text-center px-4 py-3 font-semibold">Durum</th>
              <th className="text-right px-4 py-3 font-semibold">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={4} className="p-8 text-center text-[#6B7280]">Kayıt yok</td></tr>
            ) : items.map((it) => (
              <tr key={it.id} className="border-t border-[#E5E7EB]">
                <td className="px-4 py-3 font-semibold text-[#111827]">{it.name}</td>
                <td className="px-4 py-3 text-xs text-[#6B7280]">{it.description || "-"}</td>
                <td className="px-4 py-3 text-center">
                  <Badge variant={it.is_active ? "success" : "default"}>
                    {it.is_active ? "Aktif" : "Pasif"}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => openEdit(it)} data-testid={`edit-def-${it.id}`}
                          className="p-1.5 text-[#6B7280] hover:text-[#0B3676] rounded">
                    <Edit size={14} />
                  </button>
                  {it.is_active && (
                    <button onClick={() => deactivate(it.id)}
                            data-testid={`delete-def-${it.id}`}
                            className="p-1.5 text-[#6B7280] hover:text-[#DC2626] hover:bg-[#DC2626]/10 rounded ml-1"
                            title="Sil">
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{edit ? "Düzenle" : "Yeni"} - {TITLES[kind]}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="block text-xs font-semibold text-[#111827] mb-1.5">Ad</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     data-testid="def-name-input" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#111827] mb-1.5">Açıklama</label>
              <Textarea value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button>
            <Button onClick={save} data-testid="save-def" className="bg-[#0B3676] hover:bg-[#08285A] text-white">
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
